import { Action, createHook, createStore } from "react-sweet-state";

import { authenticatedPatchFetch } from "utility/authenticated_fetch";
import { ResponseError } from "utility/response_error";

// -----------------------------------------------------------------------------

export enum PermissionLevel {
  SuperAdmin = "SuperAdmin",
  Auditor = "Auditor",
  Admin = "Admin",
  UnprivilegedUser = "UnprivilegedUser",
}

// -----------------------------------------------------------------------------

const initialState: UserStoreState = Object.freeze({
  user: Object.freeze({
    uuid: "",
    fullName: "",
    preferredName: "",
    email: "",
    features: 0,
    gravatarEmail: "",
    organization: "",
    permissionLevel: PermissionLevel.UnprivilegedUser,
    preferences: Object.freeze({
      productAndFeatures: null,
      companyAnnouncements: null,
      educationAndTraining: null,
      marketingInformation: null,
      monthlyNewsletter: null,
      sourceConnected: null,
      sourceDisconnected: null,
      destinationConnected: null,
      destinationDisconnected: null,
      scanFailure: null,
    }),
  }),
  loadAttempted: false,
  loading: false,
  savedEdit: Object.freeze({
    success: undefined,
    errorMsg: "",
  }),
  isFreeTrialBarDismissed: false,
});

// -----------------------------------------------------------------------------

export const UserStore = createStore({
  initialState,
  actions: {
    set,
    clear,

    edit,
    dismissFreeTrialBar,
    clearSavedEdit,
  },
});

export const useUserStore = createHook(UserStore);

// -----------------------------------------------------------------------------

function set(user: Record<string, any>): Action<UserStoreState> {
  return ({ setState }) => setState({ user: buildUserData(user) });
}

function clear(): Action<UserStoreState> {
  return ({ setState }) => setState(initialState);
}

function edit(newData: object): Action<UserStoreState> {
  return async ({ getState, setState, dispatch }) => {
    let payload;
    let success;
    try {
      setState({ savedEdit: initialState.savedEdit });
      payload = await authenticatedPatchFetch(
        `api/account/${getState().user.uuid}`,
        newData
      );
      success = true;
    } catch (error) {
      payload = (error as ResponseError).payload;
    }

    //
    dispatch(setMeResponse(payload, success));
  };
}

function dismissFreeTrialBar(): Action<UserStoreState> {
  return async ({ setState }) => setState({ isFreeTrialBarDismissed: true });
}

function clearSavedEdit(): Action<UserStoreState> {
  return async ({ setState }) =>
    setState({ savedEdit: initialState.savedEdit });
}

// -----------------------------------------------------------------------------

function setMeResponse(
  data: Record<string, any>,
  editSuccess?: boolean
): Action<UserStoreState> {
  return ({ setState }) => {
    if (data.error || editSuccess === false) {
      return setState({
        savedEdit: {
          success: false,
          errorMsg: data.message,
        },
      });
    }

    //
    setState({
      user: buildUserData(data),
      savedEdit: {
        success: editSuccess,
        errorMsg: "",
      },
    });
  };
}

function buildUserData(data: Record<string, any>) {
  return {
    uuid: data.uuid,
    fullName: data.full_name,
    preferredName: data.preferred_name,
    gravatarEmail: data.gravatar_specific_email,
    email: data.email,
    features: data.features || 0, // '||' is sort of temporary - just so that the backend can implement flags out of band
    organization: data.organization,
    permissionLevel: data.permission_level,
    isNew: data.is_new,
    preferences: {
      productAndFeatures: data.product_and_features,
      companyAnnouncements: data.company_announcements,
      educationAndTraining: data.education_and_training,
      marketingInformation: data.marketing_information,
      monthlyNewsletter: data.monthly_newsletter,
      sourceConnected: data.source_connected,
      sourceDisconnected: data.source_disconnected,
      destinationConnected: data.destination_connected,
      destinationDisconnected: data.destination_disconnected,
      scanFailure: data.scan_failure,
    },
  };
}

// -----------------------------------------------------------------------------

export interface UserStoreState {
  user: {
    uuid: string;
    fullName: string;
    preferredName: string;
    email: string;
    features: number;
    gravatarEmail: string;
    organization: string;
    permissionLevel: PermissionLevel;
    isNew?: boolean;
    preferences: {
      sourceConnected: null | Date;
      sourceDisconnected: null | Date;
      scanFailure: null | Date;
      destinationConnected: null | Date;
      destinationDisconnected: null | Date;
      productAndFeatures: null | Date;
      companyAnnouncements: null | Date;
      educationAndTraining: null | Date;
      marketingInformation: null | Date;
      monthlyNewsletter: null | Date;
    };
  };
  loadAttempted: boolean;
  loading: boolean;
  savedEdit: {
    success: boolean | undefined;
    errorMsg: string;
  };
  isFreeTrialBarDismissed: boolean;
}
